import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Shield, BarChart3, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  getConsent,
  saveConsent,
  OPEN_SETTINGS_EVENT,
} from "./consent";

export function CookieConsent() {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const acceptBtnRef = useRef<HTMLButtonElement | null>(null);

  // Move focus to the primary action when the banner appears (WCAG 4.1.2 / 2.4.3)
  useEffect(() => {
    if (bannerOpen) {
      const t = setTimeout(() => acceptBtnRef.current?.focus(), 450);
      return () => clearTimeout(t);
    }
  }, [bannerOpen]);

  // Close banner on Escape (treats as "reject non-essential" stays open is also fine - we just dismiss)
  useEffect(() => {
    if (!bannerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setBannerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bannerOpen]);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      // Defer so it does not steal focus during initial paint
      const t = setTimeout(() => setBannerOpen(true), 800);
      return () => clearTimeout(t);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      const existing = getConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setAnalytics(true);
    setMarketing(true);
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setAnalytics(false);
    setMarketing(false);
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const savePreferences = () => {
    saveConsent({ analytics, marketing });
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {bannerOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6 pointer-events-none"
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-consent-title"
            aria-live="polite"
            aria-label="Cookie consent"
          >
            <div className="pointer-events-auto mx-auto max-w-5xl rounded-2xl border border-white/10 bg-[#0b0b0f]/95 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)] text-white p-5 sm:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="grid place-items-center h-10 w-10 rounded-full bg-accent/15 text-accent flex-shrink-0">
                    <Cookie className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p id="cookie-consent-title" className="font-display font-semibold text-white text-base mb-1">
                      We value your privacy
                    </p>
                    <p className="text-sm text-white/65 leading-relaxed">
                      We use cookies to enhance your experience, analyze traffic, and improve our content. You can accept all, reject non-essential, or customize your preferences. Read our{" "}
                      <Link to="/privacy" className="underline hover:text-white">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 lg:flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSettingsOpen(true)}
                    className="rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/10 text-white px-4"
                  >
                    Preferences
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={rejectAll}
                    className="rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/10 text-white px-4"
                  >
                    Reject all
                  </Button>
                  <Button
                    size="sm"
                    onClick={acceptAll}
                    ref={acceptBtnRef}
                    className="rounded-full bg-white text-[hsl(248_62%_21%)] hover:bg-white/90 px-5"
                  >
                    Accept all
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which categories of cookies you allow. You can change this anytime from the footer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <CategoryRow
              icon={<Shield className="h-4 w-4" />}
              title="Strictly necessary"
              description="Required for the site to function. Cannot be disabled."
              checked
              disabled
            />
            <CategoryRow
              icon={<BarChart3 className="h-4 w-4" />}
              title="Analytics"
              description="Help us understand how visitors use the site so we can improve it."
              checked={analytics}
              onChange={setAnalytics}
            />
            <CategoryRow
              icon={<Megaphone className="h-4 w-4" />}
              title="Marketing"
              description="Used to measure ad performance and personalize content where applicable."
              checked={marketing}
              onChange={setMarketing}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={rejectAll} className="rounded-full">
              Reject all
            </Button>
            <Button variant="outline" onClick={savePreferences} className="rounded-full">
              Save preferences
            </Button>
            <Button onClick={acceptAll} className="rounded-full bg-[hsl(248_62%_21%)] text-white hover:bg-primary/90">
              Accept all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryRow({
  icon,
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-border/60 bg-card/50 p-4">
      <div className="grid place-items-center h-9 w-9 rounded-lg bg-accent/10 text-accent flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground text-sm">{title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={title}
      />
    </div>
  );
}
